import { useEffect, useState } from "react";
import {
    computeTransferFlags,
    checkLocationUseMeetService,
    checkLocationUseTerminalSelection,
} from "../utils";

export function useTransferFlags({
    transferType,
    pickupLocationData,
    lastDestinationData,
    pickupTerminal,
    dropoffTerminal,
    setPickupFlightNo,
    setDropoffFlightNo,
    setPickupTerminal,
    setDropoffTerminal,
    meetAssistPrice,
    setMeetAssistS,
    setPickupSignHonorific,
    setPickupSign
}) {
    const [transferFlags, setTransferFlags] = useState({
        isAirportTransfer: false,
        isFromAirport: false,
        isToAirport: false,
        isStationTransfer: false,
        isFromStation: false,
        isToStation: false,
    });

    // Compute transfer flags from locations for all transfer types,
    // because by-the-hour bookings can still start/end at airport or station.
    useEffect(() => {
        const nextFlags = computeTransferFlags(pickupLocationData, lastDestinationData);

        setTransferFlags((prev) => {
            const merged = { ...prev, ...nextFlags };
            const hasChanged = Object.keys(merged).some((key) => merged[key] !== prev[key]);
            return hasChanged ? merged : prev;
        });
    }, [transferType, pickupLocationData, lastDestinationData]);

    // Side effects when transfer flags change
    useEffect(() => {
        const isPickupActive = Boolean(transferFlags?.isFromAirport) || Boolean(transferFlags?.isFromStation);
        const isDropoffActive = Boolean(transferFlags?.isToAirport) || Boolean(transferFlags?.isToStation);

        const pickupType = transferFlags?.isFromAirport ? "airport" : "station";
        const dropoffType = transferFlags?.isToAirport ? "airport" : "station";

        const pickupUseMeetService = checkLocationUseMeetService(pickupLocationData);
        const dropoffUseMeetService = checkLocationUseMeetService(lastDestinationData);
        const pickupUseTerminalSelection = checkLocationUseTerminalSelection(pickupLocationData);
        const dropoffUseTerminalSelection = checkLocationUseTerminalSelection(lastDestinationData);

        if (!isPickupActive) {
            setPickupFlightNo("");
            setPickupTerminal("");
            setPickupSign("");
            setPickupSignHonorific("");
        }

        if (!isDropoffActive) {
            setDropoffFlightNo("");
            setDropoffTerminal("");
        }

        if (isPickupActive && !pickupUseTerminalSelection && pickupTerminal) {
            setPickupTerminal("");
        }

        if (isDropoffActive && !dropoffUseTerminalSelection && dropoffTerminal) {
            setDropoffTerminal("");
        }

        setMeetAssistS((prev) => {
            const next = prev.map((item) => {
                if (item.meetAssistType === "arrival") {
                    if (!isPickupActive || !pickupUseMeetService) {
                        return { ...item, meetAssistName: "none", meetAssistPrice: 0 };
                    }
                    if (pickupType === "station" && item.meetAssistName === "vipMeetAssist") {
                        return {
                            ...item,
                            meetAssistName: "meetAssist",
                            meetAssistPrice: meetAssistPrice?.pickup?.meetAssistBasePrice || 0,
                        };
                    }
                    return item;
                }

                if (item.meetAssistType === "departure") {
                    if (!isDropoffActive || !dropoffUseMeetService) {
                        return { ...item, meetAssistName: "none", meetAssistPrice: 0 };
                    }
                    if (dropoffType === "station" && item.meetAssistName === "vipMeetAssist") {
                        return {
                            ...item,
                            meetAssistName: "meetAssist",
                            meetAssistPrice: meetAssistPrice?.dropoff?.meetAssistBasePrice || 0,
                        };
                    }
                    return item;
                }

                return item;
            });

            const hasChanged = next.some(
                (item, idx) =>
                    item.meetAssistName !== prev[idx].meetAssistName ||
                    item.meetAssistPrice !== prev[idx].meetAssistPrice
            );

            return hasChanged ? next : prev;
        });
    }, [
        transferFlags,
        pickupLocationData,
        lastDestinationData,
        pickupTerminal,
        dropoffTerminal,
        meetAssistPrice,
    ]);

    return {
        transferFlags,
        setTransferFlags,
    };
}
